import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {readHostInstanceEvidence} from '@github-ui/ahp-relay/host-instance-evidence'
import type {
  RecoveryRuntimeState,
  RelayRecoveryContext,
  RelayRecoveryObserver,
} from '@github-ui/ahp-relay/relay-client-recovery'
import {
  matchesNativeCommandPayload,
  snapshotNativeCommandPayload,
} from '@github-ui/ahp-session-client/client-command-payload'
import type {ActionEnvelope, ChatState, StateAction} from '@microsoft/agent-host-protocol'
import {createBrowserClientRecoveryTracker, MAX_RECOVERY_ITEMS} from './client-recovery'
import {
  RECOVERY_SURFACES,
  type RecoveryDisposition,
  type RecoveryItem,
  type RecoverySourceReference,
  type RecoverySurface,
} from './client-recovery-contract'
import {
  recoveryPendingMessages,
  recoverySessionId,
  recoveryStateStructure,
  recoveryTools,
} from './client-recovery-state'

type Dependencies = Parameters<typeof createBrowserClientRecoveryTracker>[0] & {id(): string; retire?(): void}
type Pending = {
  item: RecoveryItem
  payload?: ReturnType<typeof snapshotNativeCommandPayload>
  accepted: boolean
  rejected?: boolean
}
const MAX_SELECTED_VIEWS = 16

export function createClientRecoverySource(
  dependencies: Dependencies,
  context: RelayRecoveryContext,
): RelayRecoveryObserver {
  const binding = {task_id: context.taskId, session_id: context.sessionId, environment_id: context.environmentId}
  const selectedSurfaces = new Map<string, RecoverySurface | 'unknown'>()
  const selectedSurface = () => {
    const surfaces = new Set(selectedSurfaces.values())
    return surfaces.size === 1 ? (surfaces.values().next().value ?? 'unknown') : 'unknown'
  }
  const tracker = createBrowserClientRecoveryTracker(
    {...dependencies, role: () => recoverySourceRole(context)},
    {
      binding,
      source_id: dependencies.id(),
      surface: selectedSurface,
    },
  )
  const views = new Map<string, number>()
  const pending = new Map<string, Pending>()
  const preparations = new Set<string>()
  let pendingComplete = true
  let viewsComplete = true
  let expected: readonly RecoveryItem[] = []
  let replaying = false
  let hostInstance: string | undefined
  let clientId: string | undefined
  let generation = ''
  let sessionResource: string | undefined
  const sourceSessionId = () => recoverySessionId(context.snapshot().sessionResource ?? sessionResource)
  let detached = false
  let disposed = false
  const dispose = () => {
    if (disposed) return
    disposed = true
    tracker.dispose()
    dependencies.retire?.()
    views.clear()
    selectedSurfaces.clear()
    pending.clear()
    preparations.clear()
    expected = []
  }

  const bounded = () => {
    if (pending.size + preparations.size >= MAX_RECOVERY_ITEMS) {
      pendingComplete = false
      tracker.coverageLost('pending_commands')
      return false
    }
    return true
  }
  const commandId = (action: StateAction) =>
    action.type === 'chat/turnStarted'
      ? action.turnId
      : action.type === 'chat/pendingMessageSet'
        ? action.id
        : undefined
  const recordCommand = (channel: string, action: StateAction, origin: ActionEnvelope['origin'], accepted: boolean) => {
    if (channel !== context.snapshot().state?.resource) return
    const id = commandId(action)
    if (!id || (action.type !== 'chat/turnStarted' && action.type !== 'chat/pendingMessageSet')) return
    if (!pending.has(id) && !bounded()) return
    const revision =
      origin && Number.isSafeInteger(origin.clientSeq) && origin.clientSeq >= 0
        ? `${origin.clientId}/${origin.clientSeq}`
        : undefined
    const item: RecoveryItem = {
      item_kind: 'command',
      item_id: id,
      ...(revision ? {revision_id: revision} : {}),
      ...(action.type === 'chat/turnStarted' ? {turn_id: action.turnId} : {}),
    }
    pending.set(id, {
      item,
      accepted,
      payload: snapshotNativeCommandPayload(action.message),
    })
  }
  const inventory = (runtime: RecoveryRuntimeState) => {
    const state = context.snapshot().state
    const stateTools = recoveryTools(state)
    const items: RecoveryItem[] = []
    for (const message of recoveryPendingMessages(state)) {
      items.push(pending.get(message.id)?.item ?? {item_kind: 'command', item_id: message.id})
    }
    for (const value of pending.values()) {
      if (!items.some(item => item.item_kind === 'command' && item.item_id === value.item.item_id))
        items.push(value.item)
    }
    for (const id of preparations) items.push({item_kind: 'command', item_id: id})
    for (const {turnId, tool} of stateTools.tools) {
      if (tool.status === 'completed' || tool.status === 'cancelled' || tool.contributor?.kind !== 'client') continue
      if (!runtime.clientId || tool.contributor.clientId === runtime.clientId) {
        items.push({item_kind: 'client_tool', item_id: tool.toolCallId, turn_id: turnId})
      }
    }
    for (const tool of runtime.ownedTools) {
      if (!items.some(item => item.item_kind === 'client_tool' && item.item_id === tool.toolCallId)) {
        items.push({item_kind: 'client_tool', item_id: tool.toolCallId, turn_id: tool.turnId})
      }
    }
    for (const id of runtime.readyTools) {
      if (!items.some(item => item.item_kind === 'client_tool' && item.item_id === id)) {
        items.push({item_kind: 'client_tool', item_id: id})
      }
    }
    for (const id of views.keys()) items.push({item_kind: 'view', item_id: id})
    return {
      items,
      commands_manifest:
        state && pendingComplete && preparations.size === 0 ? ('complete' as const) : ('unknown' as const),
      tools_manifest:
        state && !stateTools.truncated && runtime.inventoryComplete && runtime.readyTools.length === 0
          ? ('complete' as const)
          : ('unknown' as const),
      views_manifest: viewsComplete ? ('complete' as const) : ('unknown' as const),
    }
  }
  const nativeFailure = (code: string | undefined): RecoveryDisposition['native_failure_code'] =>
    code === 'client_tool_disconnected' || code === 'client_tool_timeout' || code === 'client_tool_turn_ended'
      ? code
      : undefined
  const reconcile = (source: RecoverySourceReference, actions: readonly ActionEnvelope[] = []) => {
    const state = context.snapshot().state
    for (const item of expected) {
      if (item.item_kind === 'command') {
        const known = pending.get(item.item_id)
        if (known?.rejected) {
          tracker.disposition(item, {disposition: 'system_failed', evidence_kind: 'host_action'}, source)
          continue
        }
        const retained = recoveryPendingMessages(state).find(message => message.id === item.item_id)
        if (
          known?.item.revision_id !== item.revision_id ||
          !known?.payload ||
          !retained ||
          !matchesNativeCommandPayload(known.payload, retained.message)
        ) {
          tracker.disposition(item, {disposition: 'unresolved', evidence_kind: 'unknown'}, source)
        } else if (source.source_kind === 'snapshot' || source.source_kind === 'replay') {
          tracker.disposition(
            item,
            {
              disposition: 'retained',
              evidence_kind: source.source_kind === 'replay' ? 'replay_retained' : 'snapshot_retained',
            },
            source,
          )
        }
        // Queue removal and turn echoes are not a durable application ledger.
        // Missing native revision-to-work proof remains unresolved.
      } else if (item.item_kind === 'client_tool') {
        const applied = recoveryTools(state).tools.find(
          value => value.tool.toolCallId === item.item_id && value.turnId === item.turn_id,
        )
        if (applied?.tool.status !== 'completed') continue
        const terminal = actions.find(
          envelope =>
            envelope.action.type === 'chat/toolCallComplete' &&
            envelope.action.toolCallId === item.item_id &&
            envelope.action.turnId === item.turn_id &&
            !envelope.rejectionReason,
        )
        if (!terminal || terminal.action.type !== 'chat/toolCallComplete') continue
        if (terminal.action.result.success && applied.tool.success) {
          tracker.disposition(item, {disposition: 'completed', evidence_kind: 'host_action'}, source)
        } else {
          const code = nativeFailure(terminal.action.result.error?.code)
          if (!terminal.origin && code && applied.tool.error?.code === code) {
            tracker.disposition(
              item,
              {disposition: 'system_failed', evidence_kind: 'host_action', native_failure_code: code},
              source,
            )
          }
        }
      }
    }
  }
  const observeState = (
    provided: ChatState,
    path: 'snapshot' | 'replay' | 'unknown',
    actions: readonly ActionEnvelope[] = [],
    received?: RecoverySourceReference,
  ) => {
    const source =
      received ??
      tracker.source({
        source_kind: path === 'unknown' ? 'action' : path,
        state_path: path,
        source_session_id: sourceSessionId(),
        source_chat_id: provided.resource,
      })
    if (!source) return
    const structure = recoveryStateStructure(provided)
    if (structure === undefined) {
      tracker.coverageLost('state_application')
      return
    }
    return () => {
      const actual = context.snapshot()
      const applied = recoveryStateStructure(actual.state, provided)
      if (!applied || !actual.state) {
        tracker.blocked('state_application', 'state_unverified')
        return
      }
      if (!actual.sessionState?.chats.some(chat => chat.resource === actual.state?.resource)) {
        tracker.fail('state_application', 'binding_mismatch')
        return
      }
      tracker.stateApplied(source, sourceSessionId() ?? '', actual.state.resource, applied === structure)
      reconcile(source, actions)
    }
  }
  return {
    adopt(next) {
      if (
        disposed ||
        next.taskId !== context.taskId ||
        next.sessionId !== context.sessionId ||
        next.environmentId !== context.environmentId
      )
        return false
      context = next
      detached = false
      return true
    },
    select(viewId, candidateSurface) {
      if (!views.has(viewId) && views.size >= MAX_SELECTED_VIEWS) {
        viewsComplete = false
        tracker.coverageLost('view')
        return () => undefined
      }
      const surface = RECOVERY_SURFACES.find(value => value === candidateSurface)
      selectedSurfaces.set(viewId, surface ?? 'unknown')
      views.set(viewId, (views.get(viewId) ?? 0) + 1)
      return () => {
        const count = views.get(viewId)
        if (count && count > 1) views.set(viewId, count - 1)
        else {
          views.delete(viewId)
          selectedSurfaces.delete(viewId)
          tracker.blocked('controls', 'view_detached')
          if (detached && views.size === 0) dispose()
        }
      }
    },
    controls(viewId, proof) {
      if (!views.has(viewId) || !tracker.active) return
      const current = context.snapshot()
      if (proof.active_turn_id !== current.state?.activeTurn?.id) {
        tracker.fail('controls', 'controls_mismatch')
        return
      }
      const source = tracker.source({
        source_kind: 'controls',
        source_session_id: sourceSessionId(),
        source_chat_id: current.state?.resource,
      })
      if (source) tracker.controls(viewId, proof, source)
    },
    interrupted(trigger, _generation, runtime) {
      const current = context.snapshot()
      if (views.size === 0 || (!current.hasConnected && !current.state)) return
      if (tracker.active) return
      const prestate = inventory(runtime)
      expected = prestate.items
      tracker.begin(
        trigger,
        {...binding, ...(context.snapshot().state ? {chat_id: context.snapshot().state?.resource} : {})},
        prestate,
      )
    },
    attempt(value) {
      generation = `${context.generation}/${value}`
      tracker.attempt(generation)
    },
    credentials(value) {
      if (`${context.generation}/${value}` === generation) tracker.source({source_kind: 'credentials'})
    },
    initiated(value) {
      if (`${context.generation}/${value}` !== generation) return
      const source = tracker.source({source_kind: 'reconnect_initiated'})
      if (source) tracker.initiated(source)
    },
    handshake(_generation, nextClientId, meta, path) {
      const nextHost = readHostInstanceEvidence(meta)
      tracker.source({
        source_kind: 'handshake',
        state_path: path,
        client_relation: clientId ? (clientId === nextClientId ? 'same' : 'changed') : 'unknown',
        ...(hostInstance ? {before_host_instance_id: hostInstance} : {}),
        ...(nextHost ? {host_instance_id: nextHost, authority: 'host_instance_v1'} : {}),
      })
      hostInstance = nextHost
      clientId = nextClientId
    },
    session(resource) {
      sessionResource = resource
    },
    replay(actions) {
      if (!tracker.active) return
      const previous = context.snapshot().state
      if (!previous) {
        tracker.blocked('state_application', 'prestate_unknown')
        return
      }
      const received = tracker.source({
        source_kind: 'replay',
        state_path: 'replay',
        source_session_id: sourceSessionId(),
        source_chat_id: previous.resource,
      })
      if (!received) return
      const expectedState = new AhpChatStore()
      expectedState.applySnapshot(previous)
      for (const envelope of actions)
        if (!envelope.rejectionReason && envelope.channel === previous.resource) expectedState.applyAction(envelope)
      const provided = expectedState.getSnapshot()
      expectedState.dispose()
      if (!provided) return
      const applied = observeState(provided, 'replay', actions, received)
      replaying = true
      return () => {
        replaying = false
        applied?.()
      }
    },
    snapshot(state) {
      if (!tracker.active) {
        const present = new Set(recoveryPendingMessages(state).map(message => message.id))
        for (const [id, value] of pending) {
          if (value.accepted && !present.has(id)) pending.delete(id)
        }
        return
      }
      return observeState(state, 'snapshot')
    },
    action(envelope) {
      if (envelope.rejectionReason) {
        const id = commandId(envelope.action)
        const known = id ? pending.get(id) : undefined
        if (!id || !known) return
        if (!tracker.active) {
          pending.delete(id)
          return
        }
        known.rejected = true
        if (expected.some(item => item.item_kind === 'command' && item.item_id === id)) {
          tracker.fail('pending_commands', 'client_command_lost')
        }
        return
      }
      recordCommand(envelope.channel, envelope.action, envelope.origin, true)
      let applied: (() => void) | undefined
      if (
        tracker.active &&
        !replaying &&
        envelope.action.type.startsWith('chat/') &&
        envelope.channel === context.snapshot().state?.resource
      ) {
        const previous = context.snapshot().state
        if (previous) {
          const received = tracker.source({
            source_kind: 'action',
            state_path: 'unknown',
            source_session_id: sourceSessionId(),
            source_chat_id: envelope.channel,
          })
          if (!received) return
          tracker.stateInput(received)
          const expectedState = new AhpChatStore()
          expectedState.applySnapshot(previous)
          expectedState.applyAction(envelope)
          const provided = expectedState.getSnapshot()
          expectedState.dispose()
          if (provided) applied = observeState(provided, 'unknown', [envelope], received)
        }
      }
      return () => {
        applied?.()
        if (envelope.action.type === 'chat/turnStarted') {
          pending.delete(envelope.action.turnId)
          if (!tracker.active && envelope.action.queuedMessageId) pending.delete(envelope.action.queuedMessageId)
        }
        if (!tracker.active && envelope.action.type === 'chat/pendingMessageRemoved') pending.delete(envelope.action.id)
      }
    },
    command(channel, action, originClientId, clientSeq) {
      recordCommand(channel, action, {clientId: originClientId, clientSeq}, false)
    },
    preparing(id) {
      if (!bounded()) return () => undefined
      preparations.add(id)
      return () => preparations.delete(id)
    },
    clientToolLost(toolCallId, turnId) {
      if (
        expected.some(
          item => item.item_kind === 'client_tool' && item.item_id === toolCallId && item.turn_id === turnId,
        )
      ) {
        tracker.fail('client_tools', 'client_ownership_lost')
      }
    },
    unavailable(reason) {
      for (const stage of ['reconnect', 'state_application', 'pending_commands', 'client_tools', 'controls'] as const) {
        tracker.blocked(stage, reason)
      }
    },
    coverageLost(reason) {
      tracker.coverageLost('source', reason)
    },
    dispose() {
      detached = true
      tracker.blocked('controls', 'disposed')
      if (views.size === 0) dispose()
    },
  }
}

export function recoverySourceRole(context: RelayRecoveryContext) {
  const state = context.snapshot()
  if (!state.passiveKnown) return 'unknown'
  if (state.passive) return 'passive'
  return state.state?.interactivity === 'read-only' ? 'read_only' : state.state ? 'writable' : 'unknown'
}

export type RecoverySourceDependencies = Omit<Dependencies, 'role'>
